import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { TableTab, TableTabs } from '@/components/DataTable/TableView'
import { Dropdown, DropdownDivider, DropdownItem } from '@/components/Dropdown'
import { HoverCard } from '@/components/HoverCard'
import { Icon } from '@/components/Icon'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from '@/components/ModalSheet'
import { Portal } from '@/components/Portal'
import { useFittingItems } from '@/hooks/element-hooks'
import { dragFromPress } from '@/utils/drag-util'
import styles from './SavedFilterTabs.module.css'

export interface SavedFilterTabItem {
  id: string
  name: string
  isSelected: boolean
  /**
   * Marks a tab opened from a shared link, which isn't kept.
   */
  isShared?: boolean
  /**
   * What hovering the tab shows, or `null` for nothing.
   */
  peek: ReactNode
}

export interface SavedFilterTabsProps {
  tabs: SavedFilterTabItem[]
  nameMaxLength: number
  menuTabId: string | null
  renamingTabId: string | null
  editingTabId: string | null
  /**
   * The open tab menu's items.
   */
  menu: ReactNode
  /**
   * The open tab editor.
   */
  editor: ReactNode
  onSelect: (id: string) => void
  onOpenMenu: (id: string) => void
  onCloseMenu: () => void
  onCloseEditor: () => void
  onRename: (name: string) => void
  onCancelRename: () => void
  onMove: (id: string, beforeId: string | null) => void
}

interface DragState {
  id: string
  name: string
  top: number
  offset: number
  pointerX: number
  beforeId: string | null
}

/**
 * The tabs above the jobs list. Tabs that don't fit fold into a More menu.
 * Tabs drag sideways to reorder.
 */
export function SavedFilterTabs({
  tabs,
  nameMaxLength,
  menuTabId,
  renamingTabId,
  editingTabId,
  menu,
  editor,
  onSelect,
  onOpenMenu,
  onCloseMenu,
  onCloseEditor,
  onRename,
  onCancelRename,
  onMove,
}: SavedFilterTabsProps) {
  const selectedIndex = tabs.findIndex((t) => t.isSelected)
  const { rowRef, measureRef, shown, folded } = useFittingItems(
    tabs,
    selectedIndex,
  )
  const tabEls = useRef(new Map<string, HTMLElement>())
  const [drag, setDrag] = useState<DragState | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const updateDrag = (next: DragState | null) => {
    dragRef.current = next
    setDrag(next)
  }
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  const startDrag = (tab: SavedFilterTabItem, e: PointerEvent<HTMLElement>) => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    const offset = e.clientX - rect.left
    dragFromPress(e, {
      onMove: (ev) => {
        const beforeId =
          shown.find((t) => {
            const r = tabEls.current.get(t.id)?.getBoundingClientRect()
            return r && ev.clientX < r.left + r.width / 2
          })?.id ?? null
        updateDrag({
          id: tab.id,
          name: tab.name,
          top: rect.top,
          offset,
          pointerX: ev.clientX,
          beforeId,
        })
      },
      onEnd: () => {
        const current = dragRef.current
        updateDrag(null)
        if (current) {
          // Past the last tab in the row means before the ones folded into
          // More, so the tab stays in sight.
          onMove(current.id, current.beforeId ?? folded[0]?.id ?? null)
        }
      },
      onCancel: () => updateDrag(null),
    })
  }

  const renderTab = (tab: SavedFilterTabItem) => {
    if (tab.id === renamingTabId) {
      return (
        <RenameField
          key={tab.id}
          name={tab.name}
          maxLength={nameMaxLength}
          onRename={onRename}
          onCancel={onCancelRename}
        />
      )
    }

    const isMenuOpen = tab.id === menuTabId
    const isEditing = tab.id === editingTabId
    return (
      <span key={tab.id} className={styles.slot}>
        {drag?.beforeId === tab.id && drag.id !== tab.id && (
          <span className={styles.dropLine} />
        )}
        <Dropdown
          isOpen={isMenuOpen || isEditing}
          onRequestClose={isEditing ? onCloseEditor : onCloseMenu}
          position="bottom-start"
          allowOverflow={isEditing}
          compact={!isEditing}
          noBottomPadding
          button={
            <HoverCard
              content={tab.peek}
              isDisabled={!tab.peek || isMenuOpen || isEditing || drag !== null}
            >
              <TableTab
                ref={(el) => {
                  if (el) {
                    tabEls.current.set(tab.id, el)
                  } else {
                    tabEls.current.delete(tab.id)
                  }
                }}
                isSelected={tab.isSelected}
                isDashed={tab.isShared}
                className={cx(drag?.id === tab.id && styles.dragSource)}
                onPress={() => onSelect(tab.id)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  onOpenMenu(tab.id)
                }}
                onPointerDown={
                  tab.isShared ? undefined : (e) => startDrag(tab, e)
                }
                trailing={
                  tab.isSelected && !tab.isShared ? (
                    <RawButton
                      className={styles.caret}
                      aria-label={`${tab.name} options`}
                      aria-haspopup="menu"
                      aria-expanded={isMenuOpen}
                      onPointerDown={(e) => e.stopPropagation()}
                      onPress={() =>
                        isMenuOpen ? onCloseMenu() : onOpenMenu(tab.id)
                      }
                      testId={`job-list-saved-filter-menu-${tab.id}`}
                    >
                      <Icon name="chevron-down" size={14} />
                    </RawButton>
                  ) : undefined
                }
                testId={`job-list-saved-filter-${tab.id}`}
              >
                <span className={styles.name}>{tab.name}</span>
              </TableTab>
            </HoverCard>
          }
        >
          {isEditing ? editor : isMenuOpen ? menu : null}
        </Dropdown>
      </span>
    )
  }

  return (
    <div className={styles.root} ref={rowRef}>
      <TableTabs aria-label="Saved filters">
        {shown.map(renderTab)}
        {drag?.beforeId === null && <span className={styles.dropLine} />}
        {folded.length > 0 && (
          <Dropdown
            isOpen={isMoreOpen}
            onButtonPress={() => setIsMoreOpen(!isMoreOpen)}
            onRequestClose={() => setIsMoreOpen(false)}
            position="bottom-start"
            compact
            testId="job-list-saved-filter-more"
            button={<MoreButton count={folded.length} isOpen={isMoreOpen} />}
          >
            {folded.map((tab) => (
              <DropdownItem
                key={tab.id}
                onPress={() => {
                  setIsMoreOpen(false)
                  onSelect(tab.id)
                }}
                testId={`job-list-saved-filter-${tab.id}`}
              >
                {tab.name}
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </TableTabs>
      <div className={styles.measure} ref={measureRef} aria-hidden="true">
        {tabs.map((tab) => (
          <span key={tab.id} className={styles.measureTab}>
            <span className={styles.name}>{tab.name}</span>
            {tab.isSelected && <span className={styles.measureCaret} />}
          </span>
        ))}
        <span className={styles.measureTab}>
          More 00
          <span className={styles.measureCaret} />
        </span>
      </div>
      {drag && (
        <Portal>
          <span
            className={styles.lifted}
            style={{ left: drag.pointerX - drag.offset, top: drag.top }}
          >
            <span className={styles.name}>{drag.name}</span>
          </span>
        </Portal>
      )}
    </div>
  )
}

interface MoreButtonProps {
  count: number
  isOpen: boolean
}

function MoreButton({ count, isOpen }: MoreButtonProps) {
  return (
    <RawButton
      className={cx(styles.more, isOpen && styles.moreOpen)}
      aria-haspopup="menu"
      aria-expanded={isOpen}
    >
      More
      <span className={styles.moreCount}>{count}</span>
      <Icon name="chevron-down" size={14} />
    </RawButton>
  )
}

interface RenameFieldProps {
  name: string
  maxLength: number
  onRename: (name: string) => void
  onCancel: () => void
}

function RenameField({
  name,
  maxLength,
  onRename,
  onCancel,
}: RenameFieldProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onRename(e.currentTarget.value)
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      onCancel()
    }
  }

  return (
    <input
      className={styles.rename}
      defaultValue={name}
      maxLength={maxLength}
      aria-label="Tab name"
      autoFocus
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={onKeyDown}
      onBlur={(e) => onRename(e.currentTarget.value)}
      size={Math.max(name.length, 6)}
      data-test="job-list-saved-filter-rename"
    />
  )
}

export interface DefaultTabMenuProps {
  /**
   * `undefined` when there's no room for another tab.
   */
  onDuplicate?: () => void
  /**
   * `undefined` when it's the last tab shown, as for `onDelete`.
   */
  onHide?: () => void
  onDelete?: () => void
}

/**
 * The menu of a default tab, which can't be changed, only built on, hidden
 * or deleted.
 */
export function DefaultTabMenu({
  onDuplicate,
  onHide,
  onDelete,
}: DefaultTabMenuProps) {
  return (
    <>
      <DropdownItem
        icon={<Icon name="copy" size={14} />}
        isDisabled={!onDuplicate}
        onPress={onDuplicate}
        testId="job-list-saved-filter-duplicate"
      >
        Duplicate to customize
      </DropdownItem>
      <DropdownDivider />
      <HideAndDeleteItems onHide={onHide} onDelete={onDelete} />
    </>
  )
}

export interface OwnTabMenuProps {
  onEdit: () => void
  onRename: () => void
  /**
   * `undefined` when there's no room for another tab.
   */
  onDuplicate?: () => void
  onCopyLink: () => void
  /**
   * `undefined` when it's the last tab shown, as for `onDelete`.
   */
  onHide?: () => void
  onDelete?: () => void
}

/**
 * The menu of one of the viewer's own tabs.
 */
export function OwnTabMenu({
  onEdit,
  onRename,
  onDuplicate,
  onCopyLink,
  onHide,
  onDelete,
}: OwnTabMenuProps) {
  return (
    <>
      <DropdownItem
        icon={<Icon name="filter" size={14} />}
        onPress={onEdit}
        testId="job-list-saved-filter-edit"
      >
        Edit tab
      </DropdownItem>
      <DropdownItem
        icon={<Icon name="edit" size={14} />}
        onPress={onRename}
        testId="job-list-saved-filter-rename-item"
      >
        Rename
      </DropdownItem>
      <DropdownItem
        icon={<Icon name="copy" size={14} />}
        isDisabled={!onDuplicate}
        onPress={onDuplicate}
        testId="job-list-saved-filter-duplicate"
      >
        Duplicate
      </DropdownItem>
      <DropdownItem
        icon={<Icon name="link" size={14} />}
        onPress={onCopyLink}
        testId="job-list-saved-filter-copy-link"
      >
        Copy link
      </DropdownItem>
      <DropdownDivider />
      <HideAndDeleteItems onHide={onHide} onDelete={onDelete} />
    </>
  )
}

interface HideAndDeleteItemsProps {
  onHide?: () => void
  onDelete?: () => void
}

function HideAndDeleteItems({ onHide, onDelete }: HideAndDeleteItemsProps) {
  return (
    <>
      <DropdownItem
        icon={<Icon name="hide" size={14} />}
        isDisabled={!onHide}
        onPress={onHide}
        testId="job-list-saved-filter-hide"
      >
        Hide tab
      </DropdownItem>
      <DropdownItem
        icon={<Icon name="delete" size={14} />}
        isDisabled={!onDelete}
        onPress={onDelete}
        testId="job-list-saved-filter-delete"
      >
        Delete tab
      </DropdownItem>
    </>
  )
}

export interface DeleteTabConfirmContentProps {
  name: string
}

export function DeleteTabConfirmContent({
  name,
}: DeleteTabConfirmContentProps) {
  return (
    <ModalSheetContentBlock>
      <ModalSheetContentTitle>Delete “{name}”?</ModalSheetContentTitle>
      <ModalSheetContentDescription>
        Its filters go with it. This can’t be undone.
      </ModalSheetContentDescription>
    </ModalSheetContentBlock>
  )
}

export function ResetTabsConfirmContent() {
  return (
    <ModalSheetContentBlock>
      <ModalSheetContentTitle>Reset tabs?</ModalSheetContentTitle>
      <ModalSheetContentDescription>
        Every default tab comes back in its original order, including ones you
        removed. Your own tabs stay, after the defaults.
      </ModalSheetContentDescription>
    </ModalSheetContentBlock>
  )
}
